<!-- =========================================================================================
  File Name: ClusterStatisticsPanel.vue
  Description: Enhanced cluster statistics panel with search, pagination, and scroll
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-27
========================================================================================== -->

<template>
	<div class="cluster-statistics-panel">
		<vx-card class="mb-0">
			<!-- Header with Search -->
			<template slot="no-body">
				<div class="panel-header">
					<div class="header-title">
						<feather-icon icon="BarChart2Icon" svgClasses="w-5 h-5" />
						<h6>{{ $t("Clustering.Statistics") }}</h6>
						<span class="total-badge">{{ filteredStatistics.length }}</span>
					</div>
					<div class="flex items-center justify-end">
						<vs-button
							type="flat"
							icon-pack="feather"
							icon="icon-download"
							:title="$t('Clustering.ExportToExcel')"
							@click="exportToExcel"
						/>
						<vs-button
							type="flat"
							icon-pack="feather"
							icon="icon-x"
							:title="$t('Wizard.Close')"
							@click="closePanel"
						/>
					</div>
				</div>
			</template>

			<div class="panel-content">
				<!-- Tabs Navigation -->
				<vs-tabs v-model="activeTab" class="statistics-tabs">
					<!-- Clusters List Tab -->
					<vs-tab :label="$t('Clustering.ClustersList')" icon-pack="feather" icon="icon-list">
						<!-- Search Bar -->
						<div class="search-section">
							<vs-input
								v-model="searchQuery"
								:placeholder="$t('Clustering.SearchClusters')"
								icon-pack="feather"
								icon="icon-search"
								class="w-full"
								size="small"
							/>
						</div>

						<!-- Statistics List with Scroll -->
						<div class="statistics-list">
							<transition-group name="stat-list" tag="div">
								<div
									v-for="(stat, index) in paginatedStatistics"
									:key="stat.id"
									class="stat-card"
									:class="{active: selectedClusterId === stat.id}"
									@click="selectCluster(stat)"
									@mouseenter="hoverCluster(stat)"
									@mouseleave="unhoverCluster"
								>
									<!-- Color Badge & Label -->
									<div class="stat-header">
										<div class="flex items-center flex-1">
											<div class="color-badge-lg" :style="{backgroundColor: stat.color}">
												<span class="cluster-number">{{
													(currentPage - 1) * itemsPerPage + index + 1
												}}</span>
											</div>
											<div class="stat-info ml-3">
												<p class="stat-label">{{ stat.label }}</p>
												<p class="stat-sublabel">
													{{ stat.totalMicroClusters }} {{ $t("Clustering.MicroClusters") }}
												</p>
											</div>
										</div>
										<div class="cluster-actions">
											<vs-button
												type="flat"
												color="primary"
												size="small"
												icon-pack="feather"
												icon="icon-zoom-in"
												:title="$t('Clustering.ZoomTo')"
												@click.stop="viewCluster(stat)"
											/>
											<vs-button
												type="flat"
												color="warning"
												size="small"
												icon-pack="feather"
												icon="icon-eye"
												:title="$t('Clustering.FocusOn')"
												@click.stop="$emit('focus-cluster', stat)"
											/>
										</div>
									</div>

									<!-- Statistics Grid -->
									<div class="stat-metrics">
										<div class="metric-item">
											<feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-primary" />
											<div class="metric-content">
												<span class="metric-value">{{ stat.totalPoints }}</span>
												<span class="metric-label">{{ $t("Points") }}</span>
											</div>
										</div>

										<div class="metric-item">
											<feather-icon icon="PackageIcon" svgClasses="w-4 h-4 text-success" />
											<div class="metric-content">
												<span class="metric-value">{{ formatNumber(stat.totalDemand) }}</span>
												<span class="metric-label">{{ $t("Clustering.Demand") }}</span>
											</div>
										</div>

										<div v-if="stat.totalCapacity" class="metric-item">
											<feather-icon icon="TruckIcon" svgClasses="w-4 h-4 text-warning" />
											<div class="metric-content">
												<span class="metric-value">{{ formatNumber(stat.totalCapacity) }}</span>
												<span class="metric-label">{{ $t("Clustering.Capacity") }}</span>
											</div>
										</div>

										<div v-if="stat.usagePercentage" class="metric-item">
											<feather-icon icon="TrendingUpIcon" svgClasses="w-4 h-4 text-danger" />
											<div class="metric-content">
												<span class="metric-value">{{ stat.usagePercentage }}%</span>
												<span class="metric-label">{{ $t("Clustering.Usage") }}</span>
											</div>
										</div>
									</div>

									<!-- Progress Bar (Usage) -->
									<div v-if="stat.usagePercentage" class="usage-progress">
										<div class="progress-bar">
											<div
												class="progress-fill"
												:style="{
													width: stat.usagePercentage + '%',
													backgroundColor: getUsageColor(stat.usagePercentage),
												}"
											></div>
										</div>
									</div>
								</div>
							</transition-group>

							<!-- Empty State -->
							<div v-if="filteredStatistics.length === 0" class="empty-state">
								<feather-icon icon="InboxIcon" svgClasses="w-12 h-12 text-grey" />
								<p class="text-grey">{{ $t("Clustering.NoStatsFound") }}</p>
							</div>
						</div>
						<!-- Clear Focus Button -->
						<div v-if="focusedCluster" class="clear-focus-section">
							<vs-button
								color="danger"
								type="border"
								icon-pack="feather"
								icon="icon-x-circle"
								class="w-full"
								@click="$emit('clear-focus')"
							>
								{{ $t("Clustering.RemoveFocus") }}
							</vs-button>
						</div>

						<!-- Pagination Controls -->
						<div v-if="totalPages > 1" class="pagination-section">
							<div class="pagination-info">
								<span class="text-sm text-grey">
									{{ paginationText }}
								</span>
							</div>
							<div class="pagination-controls">
								<vs-button
									type="flat"
									size="small"
									icon-pack="feather"
									icon="icon-chevron-left"
									:disabled="currentPage === 1"
									@click="previousPage"
								/>
								<span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
								<vs-button
									type="flat"
									size="small"
									icon-pack="feather"
									icon="icon-chevron-right"
									:disabled="currentPage === totalPages"
									@click="nextPage"
								/>
							</div>
						</div>
						<!-- Summary Footer -->
						<div class="summary-footer">
							<div class="summary-item">
								<span class="summary-label">{{ $t("RouterSelection.Total") }}:</span>
								<span class="summary-value">{{ totalPoints }} {{ $t("Points") }}</span>
							</div>
							<div class="summary-item">
								<span class="summary-label">{{ $t("Clustering.Clusters") }}:</span>
								<span class="summary-value">{{ statistics.length }}</span>
							</div>
						</div>
					</vs-tab>

					<!-- Balance Metrics Tab -->
					<vs-tab :label="$t('Clustering.BalanceMetrics')" icon-pack="feather" icon="icon-activity">
						<div v-if="statistics.length > 0" class="balance-metrics-section">
							<div class="metrics-header">
								<h6 class="metrics-title">{{ $t("Clustering.BalanceMetrics") }}</h6>
								<div class="balance-indicator" :class="balanceStatus">
									<div class="indicator-dot"></div>
									<span class="indicator-text">{{ balanceLabel }}</span>
								</div>
							</div>

							<div class="metrics-grid">
								<div class="metric-card">
									<div class="metric-icon-wrapper success">
										<feather-icon icon="TrendingUpIcon" svgClasses="w-4 h-4" />
									</div>
									<div class="metric-details">
										<p class="metric-title">{{ $t("Clustering.AverageDemand") }}</p>
										<p class="metric-number">{{ formatNumber(averageDemand) }}</p>
									</div>
								</div>

								<div class="metric-card">
									<div class="metric-icon-wrapper warning">
										<feather-icon icon="ActivityIcon" svgClasses="w-4 h-4" />
									</div>
									<div class="metric-details">
										<p class="metric-title">{{ $t("Clustering.StandardDeviation") }}</p>
										<p class="metric-number">{{ formatNumber(standardDeviation) }}</p>
									</div>
								</div>

								<div class="metric-card">
									<div class="metric-icon-wrapper" :class="balanceStatus">
										<feather-icon icon="PercentIcon" svgClasses="w-4 h-4" />
									</div>
									<div class="metric-details">
										<p class="metric-title">{{ $t("Clustering.VariationCoefficient") }}</p>
										<p class="metric-number">{{ variationCoefficient }}%</p>
									</div>
								</div>
							</div>

							<!-- Demand Bar Chart -->
							<div class="flex justify-between items-center mb-2">
								<h6 class="text-sm font-semibold">{{ $t("Clustering.DemandDistribution") }}</h6>
								<feather-icon
									icon="Maximize2Icon"
									svgClasses="w-4 h-4 cursor-pointer hover:text-primary"
									:title="$t('Maximize')"
									@click="openExpandedChart"
								/>
							</div>
							<div class="chart-container" style="overflow-x: auto">
								<div
									class="chart-scroll-wrapper"
									:style="{width: Math.max(100, statistics.length * 40) + 'px', minWidth: '100%'}"
								>
									<canvas ref="demandChart" height="200"></canvas>
								</div>
							</div>
						</div>

						<!-- Empty State for Balance Tab -->
						<div v-else class="empty-state">
							<feather-icon icon="BarChart2Icon" svgClasses="w-12 h-12 text-grey" />
							<p class="text-grey">{{ $t("Clustering.NoStatsAvailable") }}</p>
						</div>

						<!-- Expanded Chart Popup -->
						<vs-popup :title="$t('Clustering.DemandDistribution')" :active.sync="showExpandedChart">
							<div class="p-4" style="overflow-x: auto">
								<div :style="{width: Math.max(100, statistics.length * 50) + 'px', minWidth: '100%'}">
									<canvas ref="expandedDemandChart" height="400"></canvas>
								</div>
							</div>
						</vs-popup>
					</vs-tab>
				</vs-tabs>
			</div>
		</vx-card>
	</div>
</template>

<script>
import Chart from "chart.js";
import * as XLSX from "xlsx";

export default {
	name: "ClusterStatisticsPanel",
	props: {
		statistics: {
			type: Array,
			default: function () {
				return [];
			},
		},
		selectedClusterId: {
			type: [Number, String],
			default: null,
		},
		itemsPerPage: {
			type: Number,
			default: 5,
		},
		focusedCluster: {
			type: [Number, String],
			default: null,
		},
	},
	data() {
		return {
			searchQuery: "",
			currentPage: 1,
			demandChart: null,
			activeTab: 0,
			showExpandedChart: false,
		};
	},
	computed: {
		/**
		 * Calculate average demand across all clusters
		 */
		averageDemand() {
			if (this.statistics.length === 0) return 0;
			const totalDemand = this.statistics.reduce((sum, stat) => sum + (stat.totalDemand || 0), 0);
			return Math.round(totalDemand / this.statistics.length);
		},

		/**
		 * Calculate standard deviation of demand
		 */
		standardDeviation() {
			if (this.statistics.length === 0) return 0;
			const avg = this.averageDemand;
			const squareDiffs = this.statistics.map((stat) => {
				const diff = (stat.totalDemand || 0) - avg;
				return diff * diff;
			});
			const avgSquareDiff = squareDiffs.reduce((sum, val) => sum + val, 0) / this.statistics.length;
			return Math.round(Math.sqrt(avgSquareDiff));
		},

		/**
		 * Calculate coefficient of variation (CV%)
		 */
		variationCoefficient() {
			if (this.averageDemand === 0) return 0.0;
			return ((this.standardDeviation / this.averageDemand) * 100).toFixed(1);
		},

		/**
		 * Determine balance status based on CV%
		 * Excellent: CV < 15%
		 * Good: CV 15-25%
		 * Fair: CV 25-40%
		 * Poor: CV > 40%
		 */
		balanceStatus() {
			const cv = parseFloat(this.variationCoefficient);
			if (cv < 15) return "excellent";
			if (cv < 25) return "good";
			if (cv < 40) return "fair";
			return "poor";
		},

		/**
		 * Get balance label text
		 */
		balanceLabel() {
			switch (this.balanceStatus) {
				case "excellent":
					return this.$t("Clustering.Balance.Excellent");
				case "good":
					return this.$t("Clustering.Balance.Good");
				case "fair":
					return this.$t("Clustering.Balance.Fair");
				case "poor":
					return this.$t("Clustering.Balance.Poor");
				default:
					return "";
			}
		},

		/**
		 * Filter statistics by search query
		 */
		filteredStatistics() {
			if (!this.statistics || !Array.isArray(this.statistics)) {
				return [];
			}

			if (!this.searchQuery) {
				return this.statistics;
			}

			const query = this.searchQuery.toLowerCase();
			return this.statistics.filter(function (stat) {
				return (
					(stat.label && stat.label.toLowerCase().includes(query)) ||
					(stat.micro_cluster_identification &&
						stat.micro_cluster_identification.toString().toLowerCase().includes(query)) ||
					(stat.id && stat.id.toString().includes(query))
				);
			});
		},

		/**
		 * Calculate total pages
		 */
		totalPages() {
			return Math.ceil(this.filteredStatistics.length / this.itemsPerPage);
		},

		/**
		 * Get paginated statistics
		 */
		paginatedStatistics() {
			if (!this.filteredStatistics) return [];
			const start = (this.currentPage - 1) * this.itemsPerPage;
			const end = start + this.itemsPerPage;
			return this.filteredStatistics.slice(start, end);
		},

		/**
		 * Pagination text
		 */
		paginationText() {
			if (!this.filteredStatistics) return "";
			const start = (this.currentPage - 1) * this.itemsPerPage + 1;
			const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredStatistics.length);
			return this.$t("Clustering.ShowingItems", {start: start, end: end, total: this.filteredStatistics.length});
		},

		/**
		 * Calculate total points across all clusters
		 */
		totalPoints() {
			if (!this.statistics || !Array.isArray(this.statistics)) return 0;
			return this.statistics.reduce(function (sum, stat) {
				return sum + (stat.totalPoints || 0);
			}, 0);
		},
	},
	watch: {
		searchQuery: function () {
			this.currentPage = 1;
		},
		statistics: {
			handler(newVal) {
				// Reset expanded chart if statistics array is empty or replaced
				if (!newVal || newVal.length === 0) {
					this.showExpandedChart = false;
				}
				if (this.currentPage > this.totalPages) {
					this.currentPage = Math.max(1, this.totalPages);
				}
				// Update chart when statistics change (only if on balance tab)
				if (this.activeTab === 1) {
					this.$nextTick(() => {
						this.updateDemandChart();
						if (this.showExpandedChart) {
							this.initExpandedDemandChart();
						}
					});
				}
			},
			deep: true,
			immediate: true,
		},
		activeTab: function (newTab) {
			// When switching to balance metrics tab, initialize chart
			if (newTab === 1) {
				this.initDemandChart();
			}
		},
	},
	mounted() {
		// Only initialize chart if starting on balance metrics tab
		if (this.activeTab === 1) {
			this.initDemandChart();
		}
	},
	beforeDestroy() {
		if (this.demandChart) {
			this.demandChart.destroy();
		}
		if (this.expandedDemandChart) {
			this.expandedDemandChart.destroy();
		}
	},
	methods: {
		/**
		 * Initialize demand chart
		 */
		initDemandChart() {
			// Wait for next tick to ensure DOM is updated
			this.$nextTick(() => {
				if (!this.$refs.demandChart) {
					return;
				}

				if (this.statistics.length === 0) {
					return;
				}

				const ctx = this.$refs.demandChart.getContext("2d");

				// Destroy existing chart if any
				if (this.demandChart) {
					this.demandChart.destroy();
					this.demandChart = null;
				}

				const labels = this.statistics.map((stat, index) => `C${index + 1}`);
				const data = this.statistics.map((stat) => stat.totalDemand || 0);
				const backgroundColors = this.statistics.map((stat) => {
					const color = stat.color || "#999999";
					return color;
				});

				this.demandChart = new Chart(ctx, {
					type: "bar",
					data: {
						labels: labels,
						datasets: [
							{
								label: this.$t("Clustering.Demand"),
								data: data,
								backgroundColor: backgroundColors,
								borderColor: backgroundColors,
								borderWidth: 1,
							},
						],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						legend: {
							display: false,
						},
						scales: {
							yAxes: [
								{
									ticks: {
										beginAtZero: true,
										fontSize: 10,
									},
									gridLines: {
										display: true,
										color: "rgba(0, 0, 0, 0.05)",
									},
								},
							],
							xAxes: [
								{
									ticks: {
										fontSize: 10,
									},
									gridLines: {
										display: false,
									},
								},
							],
						},
						tooltips: {
							enabled: true,
							callbacks: {
								label: (tooltipItem) => {
									const stat = this.statistics[tooltipItem.index];
									return [
										`${this.$t("Clustering.Demand")}: ${this.formatNumber(stat.totalDemand)}`,
										`${this.$t("Points")}: ${stat.totalPoints}`,
									];
								},
							},
						},
					},
				});
			});
		},

		openExpandedChart() {
			this.showExpandedChart = true;
			this.$nextTick(() => {
				this.initExpandedDemandChart();
			});
		},

		initExpandedDemandChart() {
			if (this.expandedDemandChart) {
				this.expandedDemandChart.destroy();
			}

			// Ensure popup content is rendered
			if (!this.$refs.expandedDemandChart) return;

			const ctx = this.$refs.expandedDemandChart.getContext("2d");
			// ... rest of the function

			const labels = this.statistics.map((stat) => stat.label);
			const data = this.statistics.map((stat) => stat.totalDemand);
			const backgroundColors = this.statistics.map((stat) => stat.color || "#999999");

			this.expandedDemandChart = new Chart(ctx, {
				type: "bar",
				data: {
					labels: labels,
					datasets: [
						{
							label: this.$t("Clustering.Demand"),
							data: data,
							backgroundColor: backgroundColors,
							borderColor: backgroundColors,
							borderWidth: 1,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					legend: {
						display: false,
					},
					scales: {
						yAxes: [
							{
								ticks: {
									beginAtZero: true,
									fontSize: 12,
								},
								gridLines: {
									display: true,
									color: "rgba(0, 0, 0, 0.05)",
								},
							},
						],
						xAxes: [
							{
								ticks: {
									fontSize: 12,
								},
								gridLines: {
									display: false,
								},
							},
						],
					},
					tooltips: {
						enabled: true,
						callbacks: {
							label: (tooltipItem) => {
								const stat = this.statistics[tooltipItem.index];
								return [
									`${this.$t("Clustering.Demand")}: ${this.formatNumber(stat.totalDemand)}`,
									`${this.$t("Points")}: ${stat.totalPoints}`,
								];
							},
						},
					},
				},
			});
		},

		/**
		 * Update demand chart
		 */
		updateDemandChart() {
			this.initDemandChart();
		},

		/**
		 * Select a cluster
		 */
		selectCluster(stat) {
			this.$emit("select-cluster", stat);
		},

		/**
		 * View cluster details
		 */
		viewCluster(stat) {
			this.$emit("view-cluster", stat);
		},

		/**
		 * Hover cluster
		 */
		hoverCluster(stat) {
			this.$emit("hover-cluster", stat);
		},

		/**
		 * Unhover cluster
		 */
		unhoverCluster() {
			this.$emit("unhover-cluster");
		},

		/**
		 * Previous page
		 */
		previousPage() {
			if (this.currentPage > 1) {
				this.currentPage--;
			}
		},

		/**
		 * Next page
		 */
		nextPage() {
			if (this.currentPage < this.totalPages) {
				this.currentPage++;
			}
		},

		/**
		 * Close panel
		 */
		closePanel() {
			this.$emit("close");
		},

		/**
		 * Export statistics to Excel
		 */
		exportToExcel() {
			try {
				// Create workbook
				const workbook = XLSX.utils.book_new();

				// Summary sheet
				const summaryData = [
					["CLUSTERING STATISTICS REPORT"],
					[""],
					["Summary Metrics"],
					["Total Clusters", this.statistics.length],
					["Total Points", this.totalPoints],
					["Average Demand per Cluster", this.averageDemand],
					["Standard Deviation", this.standardDeviation],
					["Variation Coefficient", this.variationCoefficient + "%"],
					["Balance Status", this.balanceLabel],
					[""],
				];

				const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
				XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

				// Detailed clusters sheet
				const detailsData = [
					[
						"Cluster",
						"Label",
						"Total Points",
						"Total Demand",
						"Avg Demand per Point",
						"Micro Clusters",
						"Color",
					],
				];

				this.statistics.forEach((stat, index) => {
					detailsData.push([
						index + 1,
						stat.label || `Cluster ${index + 1}`,
						stat.totalPoints || 0,
						stat.totalDemand || 0,
						stat.totalPoints > 0 ? Math.round((stat.totalDemand || 0) / stat.totalPoints) : 0,
						stat.totalMicroClusters || 0,
						stat.color || "",
					]);
				});

				const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
				XLSX.utils.book_append_sheet(workbook, detailsSheet, "Cluster Details");

				// Balance analysis sheet
				const balanceData = [
					["BALANCE ANALYSIS"],
					[""],
					["Cluster", "Demand", "Deviation from Average", "% Deviation"],
					[""],
				];

				this.statistics.forEach((stat, index) => {
					const demand = stat.totalDemand || 0;
					const deviation = demand - this.averageDemand;
					const percentDeviation =
						this.averageDemand > 0 ? ((deviation / this.averageDemand) * 100).toFixed(2) : 0;
					balanceData.push([`Cluster ${index + 1}`, demand, deviation, percentDeviation + "%"]);
				});

				const balanceSheet = XLSX.utils.aoa_to_sheet(balanceData);
				XLSX.utils.book_append_sheet(workbook, balanceSheet, "Balance Analysis");

				// Points Details sheet
				const pointsData = [["Point ID", "Latitude", "Longitude", "Demand", "Cluster", "Micro Cluster"]];

				this.statistics.forEach((stat) => {
					if (stat.points && Array.isArray(stat.points)) {
						stat.points.forEach((point) => {
							pointsData.push([
								point.identification || point.id || "",
								point.latitude || point.cy || 0,
								point.longitude || point.cx || 0,
								point.demand || 0,
								point.cluster_identification || stat.label || "",
								point.micro_cluster_id || "",
							]);
						});
					}
				});

				const pointsSheet = XLSX.utils.aoa_to_sheet(pointsData);
				XLSX.utils.book_append_sheet(workbook, pointsSheet, "Points Details");

				// Generate file name with timestamp
				const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
				const fileName = `clustering_statistics_${timestamp}.xlsx`;

				// Write file
				XLSX.writeFile(workbook, fileName);

				// Show success notification
				this.$vs.notify({
					color: "success",
					title: this.$t("Success"),
					text: this.$t("Clustering.ExportedSuccessfully"),
				});
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ExportError"),
				});
			}
		},

		/**
		 * Format number with thousands separator
		 */
		formatNumber(value) {
			if (!value && value !== 0) return "-";
			return parseFloat(value).toLocaleString(this.$i18n.locale);
		},

		/**
		 * Get color based on usage percentage
		 */
		getUsageColor(percentage) {
			if (percentage >= 90) return "#ea5455"; // Red
			if (percentage >= 70) return "#ff9f43"; // Orange
			if (percentage >= 50) return "#28c76f"; // Green
			return "#00cfe8"; // Blue
		},
	},
};
</script>

<style lang="scss" scoped>
.cluster-statistics-panel {
	position: absolute;
	top: 60px;
	left: 10px;
	width: 400px;
	max-height: calc(100vh - 120px);
	z-index: 54000;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	border-radius: 8px;

	.panel-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1.25rem 1.5rem;
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
		background: linear-gradient(to bottom, #ffffff, #f9fafb);

		.header-title {
			display: flex;
			align-items: center;
			gap: 0.75rem;

			h6 {
				margin: 0;
				font-size: 1rem;
				font-weight: 600;
				color: #2c3e50;
			}

			.total-badge {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: 24px;
				height: 24px;
				padding: 0 8px;
				background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
				color: white;
				border-radius: 12px;
				font-size: 0.75rem;
				font-weight: 600;
			}
		}
	}

	.panel-content {
		padding: 0;
		max-height: calc(100vh - 180px);

		.statistics-tabs {
			::v-deep .con-slot-tabs {
				padding: 0.5rem 1rem 0 1rem;
			}

			::v-deep .con-ul-tabs {
				border-bottom: 2px solid rgba(0, 0, 0, 0.06);
			}

			::v-deep .vs-tabs--content {
				padding: 1rem;
			}

			::v-deep .vs-tabs--btn {
				font-size: 0.875rem;
				font-weight: 600;
				padding: 0.75rem 1rem;
			}
		}
	}

	.search-section {
		margin-bottom: 1rem;
	}

	.balance-metrics-section {
		background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
		border-radius: 12px;
		padding: 1.25rem;
		border: 1px solid rgba(0, 0, 0, 0.05);

		.metrics-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 1rem;

			.metrics-title {
				font-size: 0.875rem;
				font-weight: 600;
				color: #2c3e50;
				margin: 0;
			}

			.balance-indicator {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				padding: 0.375rem 0.75rem;
				border-radius: 20px;
				font-size: 0.75rem;
				font-weight: 600;
				text-transform: uppercase;
				letter-spacing: 0.5px;

				.indicator-dot {
					width: 8px;
					height: 8px;
					border-radius: 50%;
					animation: pulse 2s ease-in-out infinite;
				}

				&.excellent {
					background: rgba(40, 199, 111, 0.15);
					color: #28c76f;
					.indicator-dot {
						background: #28c76f;
					}
				}

				&.good {
					background: rgba(0, 207, 232, 0.15);
					color: #00cfe8;
					.indicator-dot {
						background: #00cfe8;
					}
				}

				&.fair {
					background: rgba(255, 159, 67, 0.15);
					color: #ff9f43;
					.indicator-dot {
						background: #ff9f43;
					}
				}

				&.poor {
					background: rgba(234, 84, 85, 0.15);
					color: #ea5455;
					.indicator-dot {
						background: #ea5455;
					}
				}
			}
		}

		.metrics-grid {
			display: grid;
			grid-template-columns: 1fr;
			gap: 0.75rem;
			margin-bottom: 1rem;

			.metric-card {
				display: flex;
				align-items: center;
				gap: 0.75rem;
				background: white;
				border-radius: 8px;
				padding: 0.875rem;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
				transition: transform 0.2s ease;

				&:hover {
					transform: translateY(-2px);
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
				}

				.metric-icon-wrapper {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 36px;
					height: 36px;
					border-radius: 8px;
					flex-shrink: 0;

					&.success {
						background: rgba(40, 199, 111, 0.15);
						color: #28c76f;
					}

					&.warning {
						background: rgba(255, 159, 67, 0.15);
						color: #ff9f43;
					}

					&.excellent {
						background: rgba(40, 199, 111, 0.15);
						color: #28c76f;
					}

					&.good {
						background: rgba(0, 207, 232, 0.15);
						color: #00cfe8;
					}

					&.fair {
						background: rgba(255, 159, 67, 0.15);
						color: #ff9f43;
					}

					&.poor {
						background: rgba(234, 84, 85, 0.15);
						color: #ea5455;
					}
				}

				.metric-details {
					flex: 1;

					.metric-title {
						font-size: 0.75rem;
						color: #6c757d;
						margin: 0 0 0.25rem 0;
					}

					.metric-number {
						font-size: 1.125rem;
						font-weight: 700;
						color: #2c3e50;
						margin: 0;
					}
				}
			}
		}

		.chart-container {
			background: white;
			border-radius: 8px;
			padding: 1rem;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
			margin-bottom: 1rem;

			canvas {
				max-height: 200px;
			}
		}
	}

	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
			transform: scale(1);
		}
		50% {
			opacity: 0.7;
			transform: scale(1.1);
		}
	}

	.statistics-list {
		max-height: calc(100vh - 405px);
		overflow-y: auto;
		padding-right: 0.5rem;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-track {
			background: #f1f1f1;
			border-radius: 10px;
		}

		&::-webkit-scrollbar-thumb {
			background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
			border-radius: 10px;

			&:hover {
				background: linear-gradient(180deg, #5568d3 0%, #6a4091 100%);
			}
		}

		.stat-card {
			background: white;
			border: 2px solid rgba(0, 0, 0, 0.06);
			border-radius: 12px;
			padding: 1rem;
			margin-bottom: 0.75rem;
			cursor: pointer;
			transition: all 0.3s ease;

			&:hover {
				border-color: #667eea;
				box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
				transform: translateY(-2px);
			}

			&.active {
				border-color: #667eea;
				background: linear-gradient(135deg, #667eea08 0%, #764ba208 100%);
				box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
			}

			.stat-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 0.75rem;

				.cluster-actions {
					display: flex;
					position: relative;
					right: -10px;
					top: -14px;
				}

				.color-badge-lg {
					width: 48px;
					height: 48px;
					border-radius: 12px;
					display: flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
					position: relative;
					overflow: hidden;

					&::before {
						content: "";
						position: absolute;
						top: 0;
						left: 0;
						right: 0;
						bottom: 0;
						background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%);
					}

					.cluster-number {
						color: white;
						font-size: 1.125rem;
						font-weight: 700;
						text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
						position: relative;
						z-index: 1;
					}
				}

				.stat-info {
					flex: 1;

					.stat-label {
						font-size: 0.9375rem;
						font-weight: 600;
						color: #2c3e50;
						margin: 0;
						line-height: 1.3;
					}

					.stat-sublabel {
						font-size: 0.75rem;
						color: #7c8db0;
						margin: 0.25rem 0 0 0;
					}
				}
			}

			.stat-metrics {
				display: grid;
				grid-template-columns: repeat(2, 1fr);
				gap: 0.75rem;
				margin-bottom: 0.75rem;

				.metric-item {
					display: flex;
					align-items: center;
					gap: 0.5rem;

					.metric-content {
						display: flex;
						flex-direction: column;
						line-height: 1.2;

						.metric-value {
							font-size: 0.875rem;
							font-weight: 700;
							color: #2c3e50;
						}

						.metric-label {
							font-size: 0.6875rem;
							color: #7c8db0;
							text-transform: uppercase;
							letter-spacing: 0.5px;
						}
					}
				}
			}

			.usage-progress {
				margin-top: 0.5rem;

				.progress-bar {
					height: 6px;
					background: #f1f3f4;
					border-radius: 10px;
					overflow: hidden;

					.progress-fill {
						height: 100%;
						border-radius: 10px;
						transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
						background: linear-gradient(
							90deg,
							currentColor 0%,
							rgba(255, 255, 255, 0.3) 50%,
							currentColor 100%
						);
						background-size: 200% 100%;
						animation: shimmer 2s infinite;
					}
				}
			}
		}
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem 1rem;
		text-align: center;
	}

	.clear-focus-section {
		padding: 0.75rem 0;
		border-top: 1px solid rgba(0, 0, 0, 0.06);
		margin-top: 0.75rem;
	}

	.pagination-section {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.75rem 0 0.5rem 0;
		border-top: 1px solid rgba(0, 0, 0, 0.06);
		margin-top: 0.75rem;

		.pagination-info {
			.text-sm {
				font-size: 0.75rem;
			}

			.text-grey {
				color: #7c8db0;
			}
		}

		.pagination-controls {
			display: flex;
			align-items: center;
			gap: 0.5rem;

			.page-indicator {
				font-size: 0.875rem;
				font-weight: 600;
				color: #2c3e50;
				min-width: 60px;
				text-align: center;
			}
		}
	}

	.summary-footer {
		display: flex;
		justify-content: space-around;
		padding: 0.75rem 1rem;
		background: linear-gradient(135deg, #667eea08 0%, #764ba208 100%);
		border-radius: 8px;
		margin-top: 0.75rem;

		.summary-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 0.25rem;

			.summary-label {
				font-size: 0.75rem;
				color: #7c8db0;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}

			.summary-value {
				font-size: 1rem;
				font-weight: 700;
				color: #667eea;
			}
		}
	}
}

// Animations
@keyframes shimmer {
	0% {
		background-position: -200% 0;
	}
	100% {
		background-position: 200% 0;
	}
}

.stat-list-enter-active,
.stat-list-leave-active {
	transition: all 0.3s ease;
}

.stat-list-enter,
.stat-list-leave-to {
	opacity: 0;
	transform: translateX(20px);
}

.stat-list-move {
	transition: transform 0.3s ease;
}

// Responsive
@media screen and (max-width: 768px) {
	.cluster-statistics-panel {
		width: calc(100% - 40px);
		left: 20px;
		right: 20px;
		bottom: 20px;

		.panel-content {
			max-height: 300px;

			&.expanded {
				max-height: 500px;
			}
		}

		.statistics-list {
			max-height: 200px;

			&.show-more-items {
				max-height: 400px;
			}

			.stat-card {
				.stat-metrics {
					grid-template-columns: 1fr;
					gap: 0.5rem;
				}
			}
		}
	}
}
</style>
